/**
 * Typed callers for /api/v1/class-sessions (plan 07 Task 10, CS-01/03/04).
 * Create (one-off or recurring), patch, cancel, and complete sessions.
 */
import { ApiError, type ErrorEnvelope, isErrorCode, type schemas, type types } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

function unwrap<T>(data: T | ErrorEnvelope | null, error: { message: string } | null): T {
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  if (data && typeof data === 'object' && 'error' in data && (data as ErrorEnvelope).error) {
    const env = (data as ErrorEnvelope).error;
    throw new ApiError(isErrorCode(env.code) ? env.code : 'internal_error', {
      message: env.message,
      ...(env.details !== undefined ? { details: env.details } : {}),
      requestId: env.request_id,
      retriable: env.retriable,
    });
  }
  return data as T;
}

/** POST /api/v1/class-sessions — returns an array (recurring mode creates N). */
export async function createClassSessions(
  client: GymSupabaseClient,
  input: schemas.ClassSessionCreateInput,
): Promise<types.ClassSessionDTO[]> {
  const { data, error } = await client.functions.invoke<types.ClassSessionDTO[] | ErrorEnvelope>(
    'class-sessions',
    { body: input },
  );
  return unwrap(data, error);
}

/** PATCH /api/v1/class-sessions/:id. */
export async function patchClassSession(
  client: GymSupabaseClient,
  sessionId: string,
  input: schemas.ClassSessionPatchInput,
): Promise<types.ClassSessionDTO> {
  const { data, error } = await client.functions.invoke<types.ClassSessionDTO | ErrorEnvelope>(
    `class-sessions/${sessionId}`,
    { method: 'PATCH', body: input },
  );
  return unwrap(data, error);
}

export interface CancelSessionResult {
  status: 'cancelled';
  affectedBookings: number;
  waitlistExpired: number;
}

/** DELETE /api/v1/class-sessions/:id — cancels with booking/waitlist side-effects. */
export async function cancelClassSession(
  client: GymSupabaseClient,
  sessionId: string,
  reason?: string,
): Promise<CancelSessionResult> {
  const { data, error } = await client.functions.invoke<CancelSessionResult | ErrorEnvelope>(
    `class-sessions/${sessionId}`,
    { method: 'DELETE', ...(reason ? { body: { reason } } : {}) },
  );
  return unwrap(data, error);
}

export interface CompleteSessionResult {
  status: 'completed';
  noShowCount: number;
}

/** POST /api/v1/class-sessions/:id/complete — closes the session, no-shows remaining. */
export async function completeClassSession(
  client: GymSupabaseClient,
  sessionId: string,
): Promise<CompleteSessionResult> {
  const { data, error } = await client.functions.invoke<CompleteSessionResult | ErrorEnvelope>(
    `class-sessions/${sessionId}/complete`,
    { method: 'POST' },
  );
  return unwrap(data, error);
}
